import React from 'react';
import {
  ChevronRight,
  ChevronDown,
  Pencil,
  History,
  Undo2,
  Ban,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { FeatureFlag } from '@/client/types/feature-flags';
import { useUpdateFeatureFlagMutation } from '@/client/api/feature-flags/feature-flags.mutation';
import { FlatNode, flagToEntry, summarizeConditions } from './utils';

interface FeatureFlagsTreeProps {
  rows: FlatNode[];
  collapsed: Set<string>;
  onToggleCollapse: (path: string) => void;
  onEdit: (flag: FeatureFlag) => void;
  onRevert: (flag: FeatureFlag) => void;
  onHistory: (flag: FeatureFlag) => void;
}

// ─── Inline toggle ─────────────────────────────────────────────────────────────

const RowToggle: React.FC<{
  checked: boolean;
  pending: boolean;
  onChange: () => void;
}> = ({ checked, pending, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={pending}
    onClick={onChange}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
      checked ? 'bg-green-500' : 'bg-gray-300'
    }`}
  >
    {pending ? (
      <Loader2 className="mx-auto h-3 w-3 animate-spin text-white" />
    ) : (
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    )}
  </button>
);

// ─── Single row ────────────────────────────────────────────────────────────────

const FeatureFlagRow: React.FC<{
  flat: FlatNode;
  collapsed: boolean;
  onToggleCollapse: (path: string) => void;
  onEdit: (flag: FeatureFlag) => void;
  onRevert: (flag: FeatureFlag) => void;
  onHistory: (flag: FeatureFlag) => void;
}> = ({ flat, collapsed, onToggleCollapse, onEdit, onRevert, onHistory }) => {
  const { node, depth, hasChildren } = flat;
  const flag = node.flag;
  const updateMutation = useUpdateFeatureFlagMutation();

  const chips = flag ? summarizeConditions(flag.conditions) : [];
  const isVeto = flag ? !flag.enabled : false;

  const handleQuickToggle = () => {
    if (!flag) return;
    const next = !flag.enabled;
    // Full-replace: send the whole entry with just `enabled` flipped.
    updateMutation.mutate(
      { name: flag.name, entry: flagToEntry(flag, { enabled: next }) },
      {
        onSuccess: (res) => {
          toast.success(
            `${flag.name} ${next ? 'enabled' : 'disabled'}${
              res.version != null ? ` · v${res.version}` : ''
            }`,
          );
        },
        onError: (err: any) => {
          if (err?.status === 403) {
            toast.error('You do not have permission to manage feature flags.');
          } else {
            toast.error(err?.message ?? 'Failed to update the flag.');
          }
        },
      },
    );
  };

  return (
    <div
      className="group flex items-center gap-3 border-b border-gray-50 px-3 py-2.5 hover:bg-gray-50/70"
      style={{ paddingLeft: 12 + depth * 20 }}
    >
      {/* Expand / collapse */}
      <button
        type="button"
        onClick={() => hasChildren && onToggleCollapse(node.path)}
        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-gray-400 ${
          hasChildren ? 'hover:bg-gray-200 hover:text-gray-700' : 'invisible'
        }`}
        aria-label={collapsed ? 'Expand' : 'Collapse'}
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {/* Name + meta */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`truncate text-sm ${
              flag ? 'font-medium text-gray-900' : 'font-semibold text-gray-500'
            }`}
          >
            {node.segment}
          </span>

          {flag && (
            <span
              className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                flag.source === 'override'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
              title={
                flag.source === 'override'
                  ? 'Live Mongo override — can be reverted to default'
                  : 'Committed JSON default'
              }
            >
              {flag.source}
            </span>
          )}

          {isVeto && hasChildren && (
            <span
              className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600"
              title="Disabled at this level — vetoes all descendants"
            >
              <Ban className="h-3 w-3" /> vetoes children
            </span>
          )}
        </div>

        {flag?.description && (
          <p className="mt-0.5 truncate text-xs text-gray-400">{flag.description}</p>
        )}

        {chips.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {chips.map((c) => (
              <span
                key={c}
                className="inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-600"
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Enabled toggle */}
      {flag ? (
        <RowToggle
          checked={flag.enabled}
          pending={updateMutation.isPending}
          onChange={handleQuickToggle}
        />
      ) : (
        <span className="w-9 flex-shrink-0" />
      )}

      {/* Actions */}
      <div className="flex w-[92px] flex-shrink-0 items-center justify-end gap-0.5">
        {flag ? (
          <>
            <button
              type="button"
              onClick={() => onEdit(flag)}
              className="rounded p-1.5 text-gray-400 opacity-0 hover:bg-gray-200 hover:text-gray-700 group-hover:opacity-100"
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onHistory(flag)}
              className="rounded p-1.5 text-gray-400 opacity-0 hover:bg-gray-200 hover:text-gray-700 group-hover:opacity-100"
              title="View history"
            >
              <History className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onRevert(flag)}
              disabled={flag.source !== 'override'}
              className="rounded p-1.5 text-gray-400 opacity-0 hover:bg-amber-100 hover:text-amber-700 group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-0"
              title={
                flag.source === 'override'
                  ? 'Revert to default'
                  : 'Only overrides can be reverted'
              }
            >
              <Undo2 className="h-4 w-4" />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

const FeatureFlagsTree: React.FC<FeatureFlagsTreeProps> = ({
  rows,
  collapsed,
  onToggleCollapse,
  onEdit,
  onRevert,
  onHistory,
}) => {
  return (
    <div className="divide-y divide-gray-50">
      {rows.map((flat) => (
        <FeatureFlagRow
          key={flat.node.path}
          flat={flat}
          collapsed={collapsed.has(flat.node.path)}
          onToggleCollapse={onToggleCollapse}
          onEdit={onEdit}
          onRevert={onRevert}
          onHistory={onHistory}
        />
      ))}
    </div>
  );
};

export default FeatureFlagsTree;
